import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import momentTz from "moment-timezone";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import type {
	IncentiveRow,
	DayTotals,
	IncentiveReportSummary,
} from "../models/IncentiveRow";
import type { IncentiveShiftOverride } from "../models/IncentiveShiftOverride";
import type {
	CrewPairSummary,
	CrewPivotRow,
	CrewWeeklySummary,
	IncentiveRotationStatus,
	RotationMeta,
	TankData,
	TankRawData,
	WeeklyIncentiveData,
} from "../models/WeeklyIncentiveData";
import { INCENTIVE_ROTATION_STATUS } from "../models/WeeklyIncentiveData";
import {
	DEFAULT_INCENTIVE_THRESHOLDS,
	type SixZoneThresholds,
} from "../constants/colors";
import type { ShiftIncentiveEventSnapshotRow } from "../models/ShiftIncentiveEvent";

interface RawShiftIncentiveEventSnapshotRow
	extends Omit<
		ShiftIncentiveEventSnapshotRow,
		"EventStart" | "EventEnd" | "OverlapStart" | "OverlapEnd"
	> {
	EventStart: string | null;
	EventEnd: string | null;
	OverlapStart: string | null;
	OverlapEnd: string | null;
}

interface RawIncentiveRow
	extends Omit<IncentiveRow, "LastOverrideAt" | "LastAdjustmentAt"> {
	LastOverrideAt: string | null;
	LastAdjustmentAt: string | null;
}

/** Formats a ProductionDate string (e.g. "2026-05-26") as "Mon 05/26". */
const formatProdDate = (isoDate: string): string =>
	momentTz(isoDate.substring(0, 10), "YYYY-MM-DD").format("ddd MM/DD");

/** RS7 raw row shape from the SP. */
interface RS7RawRow {
	RowType: string;
	BlockId: number | null;
	ProductionDate: string | null;
	CrewA: number | null;
	CrewB: number | null;
	CrewC: number | null;
	CrewD: number | null;
	TotalAB: number | null;
	TotalCD: number | null;
	IsFutureDay: boolean | null;
	CurrentBlockStart: string | null;
	CurrentBlockEnd: string | null;
	CurrentCrewPair: string | null;
	PrevBlockStart: string | null;
	PrevBlockEnd: string | null;
	PrevCrewPair: string | null;
	// Raw finished/target per crew pair for weighted total computation
	FinishedAB: number | null;
	TargetAB: number | null;
	FinishedCD: number | null;
	TargetCD: number | null;
}

interface CrewRotationNavigationRow {
	RotationStatus: string;
	SelectedProductionDate: string;
	CurrentBlockStart: string | null;
	PreviousAnchor: string | null;
	NextAnchor: string | null;
	CanNavigatePrevious: boolean;
	CanNavigateNext: boolean;
}

type TimestampField = "EventStart" | "EventEnd" | "OverlapStart" | "OverlapEnd";

const EVENT_TIMESTAMP_FIELDS: TimestampField[] = [
	"EventStart",
	"EventEnd",
	"OverlapStart",
	"OverlapEnd",
];

const parseUtcDate = (
	value: string | null | undefined,
	moment: ReturnType<typeof getMomentTz>,
) => (value ? moment.utc(value).toDate() : null);

const getIncentiveSchemaByAssetId = (assetId: number): "M1" | "M2" =>
	assetId === MILL_1.id ? "M1" : "M2";

const isIncentiveRotationStatus = (
	status: string | undefined,
): status is IncentiveRotationStatus =>
	Object.values(INCENTIVE_ROTATION_STATUS).some((value) => value === status);

const mapShiftIncentiveEventSnapshotRows = (
	rows: RawShiftIncentiveEventSnapshotRow[],
	moment: ReturnType<typeof getMomentTz>,
): ShiftIncentiveEventSnapshotRow[] =>
	rows.flatMap((row) => {
		const mappedRow: ShiftIncentiveEventSnapshotRow = {
			...row,
			EventStart: null,
			EventEnd: null,
			OverlapStart: null,
			OverlapEnd: null,
		};

		for (const field of EVENT_TIMESTAMP_FIELDS) {
			mappedRow[field] = parseUtcDate(row[field], moment);
		}

		return mappedRow;
	});

/** Builds rotation-based crew pivot from RS7 META + DATA rows. */
const buildCrewPivotFromRotation = (
	rs7Rows: RS7RawRow[],
): { meta: RotationMeta; pivot: CrewPivotRow[] } | null => {
	if (!rs7Rows || rs7Rows.length === 0) return null;

	const metaRow = rs7Rows.find((r) => r.RowType === "META");
	if (!metaRow || !metaRow.CurrentBlockStart || !metaRow.CurrentCrewPair)
		return null;

	const meta: RotationMeta = {
		currentBlockStart: metaRow.CurrentBlockStart,
		currentBlockEnd: metaRow.CurrentBlockEnd!,
		currentCrewPair: metaRow.CurrentCrewPair as "AB" | "CD",
		prevBlockStart: metaRow.PrevBlockStart ?? "",
		prevBlockEnd: metaRow.PrevBlockEnd ?? "",
		prevCrewPair: (metaRow.PrevCrewPair as "AB" | "CD") ?? "CD",
	};

	const dataRows = rs7Rows.filter(
		(r) => r.RowType === "DATA" && r.ProductionDate != null,
	);
	if (dataRows.length === 0) return null;

	// Day rows: SP already sends null (no shift) vs 0 (shift, no production).
	// Frontend just marks future days so the pill renderer shows gray.
	const pivotRows: CrewPivotRow[] = dataRows.map((r) => {
		const isFuture = !!r.IsFutureDay;
		const futureKeys = new Set<string>();
		if (isFuture) {
			if (r.CrewA != null) futureKeys.add("crewA");
			if (r.CrewB != null) futureKeys.add("crewB");
			if (r.CrewC != null) futureKeys.add("crewC");
			if (r.CrewD != null) futureKeys.add("crewD");
			if (r.TotalAB != null) futureKeys.add("totalAB");
			if (r.TotalCD != null) futureKeys.add("totalCD");
		}

		return {
			date: formatProdDate(r.ProductionDate!),
			crewA: r.CrewA,
			crewB: r.CrewB,
			crewC: r.CrewC,
			crewD: r.CrewD,
			totalAB: r.TotalAB,
			totalCD: r.TotalCD,
			isTotal: false,
			futureKeys,
			blockId: r.BlockId ?? undefined,
		};
	});

	// Total row: SUM(finished) / SUM(target) per pair.
	// target=0 → null → "—" gray pill (can't divide by zero).
	const pairTotals = { AB: { fin: 0, tgt: 0 }, CD: { fin: 0, tgt: 0 } };
	for (const r of dataRows) {
		pairTotals.AB.tgt += r.TargetAB ?? 0;
		pairTotals.CD.tgt += r.TargetCD ?? 0;
		if (!r.IsFutureDay) {
			pairTotals.AB.fin += r.FinishedAB ?? 0;
			pairTotals.CD.fin += r.FinishedCD ?? 0;
		}
	}

	const pct = (fin: number, tgt: number) =>
		tgt > 0 ? (fin / tgt) * 100 : null;

	pivotRows.push({
		date: "Total",
		crewA: null,
		crewB: null,
		crewC: null,
		crewD: null,
		totalAB: pct(pairTotals.AB.fin, pairTotals.AB.tgt),
		totalCD: pct(pairTotals.CD.fin, pairTotals.CD.tgt),
		isTotal: true,
		futureKeys: new Set<string>(),
	});

	return { meta, pivot: pivotRows };
};

/**
 * Returns selected production-date incentive data from the mill-specific GetIncentiveByWeek procedure.
 * RS1: shift rows, RS2: day totals, RS3: crew weekly, RS4: crew pairs,
 * RS5: tanks raw, RS6: metadata, RS7: rotation-based crew pivot.
 * Frontend builds: tank labels and crew pivot presentation.
 */
export const getProductionDateIncentive = async (
	productionDate: string,
	assetId: number,
	signal?: AbortSignal,
): Promise<WeeklyIncentiveData> => {
	const moment = getMomentTz();
	const apiService = new MESApiService();
	const schema = getIncentiveSchemaByAssetId(assetId);
	const parameters: spParameter[] = [
		{ name: "ProductionDate", value: productionDate },
		{ name: "AssetId", value: assetId },
	];
	const resp = await apiService.callV2(
		`[${schema}].[GetIncentiveByWeek]`,
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error loading incentive dashboard");
	}
	const tables = get(resp, "data.tables", {});

	const dailyRows = (get(tables, "0.rows", []) as RawIncentiveRow[]).map(
		(r) => ({
			...r,
			LastOverrideAt: r.LastOverrideAt
				? moment.utc(r.LastOverrideAt).toDate()
				: null,
			LastAdjustmentAt: r.LastAdjustmentAt
				? moment.utc(r.LastAdjustmentAt).toDate()
				: null,
		}),
	) as IncentiveRow[];

	const dayTotals = get(tables, "1.rows", []) as DayTotals[];
	const crewWeekly = get(tables, "2.rows", []) as CrewWeeklySummary[];
	const crewPairs = get(tables, "3.rows", []) as CrewPairSummary[];
	const tanksRaw = get(tables, "4.rows", []) as TankRawData[];

	// Build tank presentation data from raw SP response
	/** Formats ProductionDate (DATE from SP) as MM/dd without timezone shift */
	const fmtDate = (v: string | null): string => {
		if (!v) return "";
		return moment(String(v).substring(0, 10), "YYYY-MM-DD").format("MM/DD");
	};

	const tanks: TankData[] = tanksRaw.map((t) => {
		let label = "";
		let sublabel = "";
		const shiftLabel = t.Shift ?? "";

		switch (t.TankKey) {
			case "current": {
				label = "Current Shift";
				if (t.ProductionDate && t.Crew) {
					sublabel = `${fmtDate(t.ProductionDate)} ${shiftLabel} — Crew ${t.Crew}`;
				}
				break;
			}
			case "last": {
				label = "Last Shift";
				if (t.ProductionDate && t.Crew) {
					sublabel = `${fmtDate(t.ProductionDate)} ${shiftLabel} — Crew ${t.Crew}`;
				}
				break;
			}
			case "weekAB": {
				label = "Week A/B";
				break;
			}
			case "weekCD": {
				label = "Week C/D";
				break;
			}
		}

		return {
			tankKey: t.TankKey,
			label,
			sublabel,
			finished: t.Finished ?? 0,
			target: t.Target ?? 0,
			incPercent: t.IncPercent ?? null,
		};
	});

	// Parse RS7 (rotation-based crew pivot) — index 6
	const rs7Rows = get(tables, "6.rows", []) as RS7RawRow[];
	const rotationResult = buildCrewPivotFromRotation(rs7Rows);

	const crewPivot = rotationResult?.pivot ?? [];
	const rotationMeta = rotationResult?.meta ?? null;
	const rotationNavigationRow = get(
		tables,
		"5.rows.0",
		null,
	) as CrewRotationNavigationRow | null;
	if (
		!rotationNavigationRow ||
		!isIncentiveRotationStatus(rotationNavigationRow.RotationStatus)
	) {
		throw new Error("Invalid crew rotation navigation response");
	}
	const thresholds =
		(get(tables, "7.rows.0", null) as SixZoneThresholds | null) ??
		DEFAULT_INCENTIVE_THRESHOLDS;

	return {
		dailyRows,
		dayTotals,
		crewWeekly,
		crewPairs,
		tanks,
		thresholds,
		crewPivot,
		rotationMeta,
		rotationNavigation: {
			status: rotationNavigationRow.RotationStatus,
			selectedProductionDate: rotationNavigationRow.SelectedProductionDate,
			currentBlockStart: rotationNavigationRow.CurrentBlockStart,
			previousAnchor: rotationNavigationRow.PreviousAnchor,
			nextAnchor: rotationNavigationRow.NextAnchor,
			canNavigatePrevious: rotationNavigationRow.CanNavigatePrevious,
			canNavigateNext: rotationNavigationRow.CanNavigateNext,
		},
	};
};

/**
 * Returns incentive report data for a date range + optional crew.
 * RS1: shift-level rows, RS2: summary totals (filtered by FinishedTons > 0 in SP).
 */
export const getIncentiveReport = async (
	startDate: string,
	endDate: string,
	crews: string[],
	shift: string | null,
	assetId: number,
	signal?: AbortSignal,
): Promise<{
	rows: IncentiveRow[];
	summary: IncentiveReportSummary;
	eventSnapshots: ShiftIncentiveEventSnapshotRow[];
}> => {
	const moment = getMomentTz();
	const apiService = new MESApiService();
	const schema = getIncentiveSchemaByAssetId(assetId);
	const crewParam = crews && crews.length > 0 ? crews.join(",") : null;
	const parameters: spParameter[] = [
		{ name: "StartDate", value: startDate },
		{ name: "EndDate", value: endDate },
		{ name: "AssetId", value: assetId },
		...(crewParam ? [{ name: "Crew", value: crewParam }] : []),
		...(shift ? [{ name: "Shift", value: shift }] : []),
	];
	const resp = await apiService.callV2(
		`[${schema}].[GetIncentiveReport]`,
		parameters,
		signal,
	);
	const tables = get(resp, "data.tables", {});

	const rows = (get(tables, "0.rows", []) as RawIncentiveRow[]).map((r) => ({
		...r,
		LastOverrideAt: r.LastOverrideAt
			? moment.utc(r.LastOverrideAt).toDate()
			: null,
		LastAdjustmentAt: r.LastAdjustmentAt
			? moment.utc(r.LastAdjustmentAt).toDate()
			: null,
	})) as IncentiveRow[];

	const summary = get(tables, "1.rows.0", {}) as IncentiveReportSummary;
	const eventSnapshots = mapShiftIncentiveEventSnapshotRows(
		get(tables, "2.rows", []) as RawShiftIncentiveEventSnapshotRow[],
		moment,
	);

	return {
		rows,
		summary,
		eventSnapshots,
	};
};

/**
 * Saves a shift override (target tons, adjustment, notes).
 * Calls the mill-specific UpsertIncentiveOverride procedure.
 */
export const updateShiftOverride = async (
	payload: IncentiveShiftOverride,
	assetId: number,
): Promise<void> => {
	const apiService = new MESApiService();
	const schema = getIncentiveSchemaByAssetId(assetId);
	const parameters: spParameter[] = [
		{ name: "ShiftId", value: payload.ShiftId },
		{ name: "TargetTonsOverride", value: payload.TargetTonsOverride },
		{ name: "Adjustment", value: payload.Adjustment },
		{ name: "Notes", value: payload.Notes },
	];
	const resp = await apiService.callV2(
		`[${schema}].[UpsertIncentiveOverride]`,
		parameters,
	);
	if (!resp.ok) throw new Error(resp.message || "Error saving override");
};
